import React, { useState } from 'react';
import axios from 'axios';
import { ChevronDown, ChevronUp, Wallet } from 'lucide-react';
import { getBackendUrl } from '../../services/origin';

const API = getBackendUrl();

/* ── VOUCHER CARD ──────────────────────────────────────────── */
export function VoucherCard({ reward, token, pin, brand, tc, ts, pc, typeColors }) {
  const [expanded, setExpanded] = useState(false);
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(false);

  const amount = Number(reward.voucher_amount || 0);
  const balance = Number(reward.voucher_balance != null ? reward.voucher_balance : amount);
  const used = Math.max(0, amount - balance);
  const usedPct = amount > 0 ? Math.min(100, Math.round((used / amount) * 100)) : 0;
  const minPerUse = reward.voucher_min_per_use;
  const color = typeColors.coupon_voucher || '#FFAA00';
  const fmt = (v) => `${Number(v || 0).toFixed(2).replace('.', ',')} €`;

  const toggle = async () => {
    const next = !expanded;
    setExpanded(next);
    if (next && history === null && !loading) {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/api/my/${token}/voucher/${reward.id}/history`, { params: { pin } });
        setHistory(res.data);
      } catch {
        setHistory({ uses: [] });
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div data-testid={`voucher-card-${reward.id}`} className="rounded-2xl p-4 border" style={{ backgroundColor: `${tc}06`, borderColor: `${color}30` }}>
      <div className="flex items-center gap-3 mb-3">
        <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${color}20` }}>
          <Wallet size={20} style={{ color }} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold truncate" style={{ color: tc }} data-testid="voucher-description">{reward.description || 'Buono monetario'}</p>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="text-[10px] font-semibold px-2 py-0.5 rounded-lg" style={{ backgroundColor: `${color}20`, color }}>Buono</span>
            {minPerUse ? (
              <span className="text-[10px]" style={{ color: `${ts}aa` }}>Min. {fmt(minPerUse)} a utilizzo</span>
            ) : null}
          </div>
        </div>
        <div className="text-right flex-shrink-0">
          <p className="text-xs" style={{ color: ts }}>Saldo</p>
          <p className="text-base font-bold tabular-nums" style={{ color }} data-testid="voucher-balance">{fmt(balance)}</p>
        </div>
      </div>

      {amount > 0 && (
        <div className="mb-2">
          <div className="flex items-center justify-between text-[10px] mb-1" style={{ color: `${ts}aa` }}>
            <span>Utilizzato {fmt(used)}</span>
            <span>{fmt(amount)} totali</span>
          </div>
          <div className="h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: `${tc}10` }}>
            <div className="h-full rounded-full transition-all" style={{ width: `${usedPct}%`, backgroundColor: color }} />
          </div>
        </div>
      )}

      <button
        type="button"
        onClick={toggle}
        data-testid={`voucher-history-toggle-${reward.id}`}
        className="w-full mt-2 h-8 rounded-lg flex items-center justify-center gap-1 text-[11px] font-medium transition-colors"
        style={{ backgroundColor: `${color}10`, color }}
      >
        {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        {expanded ? 'Nascondi storico' : 'Mostra storico utilizzi'}
      </button>

      {expanded && (
        <div className="mt-3 space-y-1.5" data-testid={`voucher-history-${reward.id}`}>
          {loading && (
            <p className="text-[11px] text-center py-2" style={{ color: ts }}>Caricamento…</p>
          )}
          {!loading && history && history.uses && history.uses.length === 0 && (
            <p className="text-[11px] text-center py-2" style={{ color: `${ts}aa` }}>Nessun utilizzo registrato</p>
          )}
          {!loading && history && history.uses && history.uses.map((u, idx) => (
            <div key={idx} data-testid={`voucher-use-${idx}`} className="rounded-xl p-2.5 border flex items-center gap-3" style={{ backgroundColor: `${tc}04`, borderColor: `${tc}08` }}>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold tabular-nums" style={{ color: tc }}>-{fmt(u.amount_used)}</p>
                <p className="text-[10px]" style={{ color: `${ts}88` }}>
                  {u.used_at ? new Date(u.used_at).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                  {u.staff_name ? ` · ${u.staff_name}` : ''}
                </p>
                {u.note && <p className="text-[10px] mt-0.5 italic" style={{ color: `${ts}66` }}>{u.note}</p>}
              </div>
              <span className="text-[10px] tabular-nums flex-shrink-0" style={{ color: `${ts}aa` }}>resta {fmt(u.balance_after)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
